import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query, queryAll, state} from "lit/decorators.js";
import Styles from "./dialog.lit.scss";
import {UnLitElement} from "../../../common/elements";
import {Dictionary, type DictionaryElement} from "../dictionary";
import {EventBus} from "../../../common/eventBus";
import {resolve} from "../../../container";
import {eventOccurredOutside, handleActivationKey, KEYS} from "../../../common/utils/events";
import {Page} from "../../../common/page";
import {waitTime} from "../../../common/utils/promises";
import type {Nullable} from "../../../bootstrap/common/types";
import {ClickTracking} from "../../../tracking/clickTracking";

export const DIALOG_OPENED_EVENT = "dialogOpened";
export const DIALOG_CLOSED_EVENT = "dialogClosed";

export const CLOSE_DIALOG = "closeDialog";

const PANEL_SIZE_FACTOR = 1.5;
export const CLOSING_ANIMATION_DURATION_IN_MILLIS = 500;

@customElement("eop-modal-dialog")
export class EopModalDialog extends UnLitElement {

    public static readonly styles = Styles;

    @queryAll(".layer-section")
    private layerSections: NodeListOf<HTMLElement>;
    @queryAll(".close")
    private closeButtons: NodeListOf<HTMLButtonElement>;
    @query("dialog")
    private dialog: HTMLDialogElement;
    @query(".dialog-panel")
    private panel: HTMLElement;
    @query(".close-button")
    private closeButton: HTMLElement;
    @property({attribute: "disable-close-on-outside-click", type: Boolean})
    public disableCloseOnOutsideClick: boolean = false;
    @state()
    private active: boolean = false;

    public constructor(
        private dialogContent: HTMLElement,
        private dictionary: Dictionary,
        disableCloseOnOutsideClick: boolean = false,
        private eventBus: EventBus = resolve(EventBus),
        private page: Page = resolve(Page),
        private clickTracking: ClickTracking = resolve(ClickTracking)
    ) {
        super();
        this.disableCloseOnOutsideClick = this.disableCloseOnOutsideClick || disableCloseOnOutsideClick;

        (this as DictionaryElement)._dictionary = dictionary;
        if (!dictionary) {
            this.dictionary = Dictionary.of(this);
        }

        this.addEventListener(CLOSE_DIALOG, () => this.close());
    }

    public render(): TemplateResult {
        return html`
            <dialog ?active=${this.active}
                    closedby="none"
                    @click=${this.closeOnOutsideClick}
                    @keydown=${this.closeOnEscape}
                    class="vertically-scrollable-area"
            >
                <div class="dialog-container">
                    <div class="dialog-panel">
                        <button type="button"
                                class="close-button"
                                aria-label=${this.dictionary.translate("MSG_CLOSE_DIALOG")} 
                                @click=${this.closeWithTracking}
                        ></button>
                        ${this.dialogContent}
                        ${this.renderSecondaryCloseButton()}
                    </div>
                    <eop-tab-out-listener .callback=${() => this.close()}></eop-tab-out-listener>
            </dialog>
        `;
    }

    public isActive(): boolean {
        return this.active;
    }

    private renderSecondaryCloseButton(): Nullable<TemplateResult> {
        if (!this.panel || this.panel?.clientHeight <= this.page.viewportHeight() * PANEL_SIZE_FACTOR) {
            return null;
        }
        return html`
            <div class="bottom-close-section layer-section ${this.backgroundClassFromSectionAbove()}">
                <button class="secondary" @click=${this.closeWithTracking}>
                    <eop-msg key="MSG_CLOSE_DIALOG"></eop-msg>
                </button>
            </div>
        `;
    }

    public connectedCallback(): void {
        super.connectedCallback();
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        Array.from(this.closeButtons).forEach(button => {
            button.addEventListener("click", event => this.closeWithTracking(event));
            button.addEventListener("keydown", (e: KeyboardEvent) => {
                handleActivationKey(e, () => this.closeWithTracking(e));
            });
        });
    }

    public async showModal(): Promise<void> {
        await this.updateComplete;
        this.dialog.showModal();
        this.active = true;

        this.eventBus.dispatchEvent(DIALOG_OPENED_EVENT, this);

        await this.focusCloseButtonWhenReady();
    }

    private async focusCloseButtonWhenReady(): Promise<void> {
        await waitTime(0);
        this.closeButton.focus({preventScroll: true});
    }

    public async close(): Promise<void> {
        this.active = false;
        this.cleanUp();
        // remove once safari and firefox support overlay property in transitions, which can be waited on with transitionend
        // https://developer.mozilla.org/en-US/docs/Web/CSS/overlay
        await waitTime(CLOSING_ANIMATION_DURATION_IN_MILLIS);
    }

    private async closeWithTracking(event: Event): Promise<void> {
        this.clickTracking.handleButtonInteract(event, "modal-dialog-close");

        await this.close();
    }

    private cleanUp(): void {
        this.dialog.close();
        this.eventBus.dispatchEvent(DIALOG_CLOSED_EVENT, this);
    }

    private closeOnOutsideClick(e: Event): void {
        if (!this.disableCloseOnOutsideClick && eventOccurredOutside(e, this.panel)) {
            void this.closeWithTracking(e);
        }
    }

    private closeOnEscape(e: KeyboardEvent): void {
        if (e.key === KEYS.ESCAPE) {
            void this.closeWithTracking(e);
        }
    }

    private backgroundClassFromSectionAbove(): string {
        if (this.layerSections.length <= 0) {
            return "";
        }

        const classes = this.layerSections.item(this.layerSections.length - 1).classList.values();
        for (const className of classes) {
            if (className.includes("background-")) {
                return className;
            }
        }
        return "";
    }
}